/**
 * SHER-1128 (SHER-308 Phase 4) — shared chrome AND interaction skeleton for the editable
 * billing grids.
 *
 * The price grid and the assignment grid are both a styled ReactGrid with the same look and the
 * same editing contract: the action column is always first, its footer cell appends a row, and a
 * data row's action cell removes the row when empty or toggles it for deletion. That shell and
 * that contract live here once, so the two components stay thin wrappers that differ only where
 * they genuinely differ — their columns, their cell writes, and (for the price grid) the add-list
 * affordance.
 */
import { useState, useEffect, useCallback } from 'react';
import { Id } from '@silevis/reactgrid';

/** The action column is the first column of every editable billing grid. */
export const ACTION_COLUMN_ID = 'action';
/** Row ids ReactGrid uses for the sticky header and the add-row footer. */
export const HEADER_ROW_ID = 'header';
export const ADD_ROW_FOOTER_ID = 'add-row-footer';

/** The styled container every editable billing grid wraps its ReactGrid in (pass `id` per grid). */
export const gridContainerSx = {
    width: '100%',
    height: '600px',
    overflow: 'auto',
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    '& .rg-cell:first-of-type': {
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        textAlign: 'center',
        fontSize: '18px',
        '&:hover': { backgroundColor: '#f0f0f0' },
    },
    '& .deleted-row': {
        backgroundColor: '#ffcccc !important',
        textDecoration: 'line-through',
        opacity: 0.8,
        color: '#666',
    },
    // SHER-714: an inactive insurer stays listed, greyed; its Status cell is the toggle.
    '& .inactive-row': {
        backgroundColor: '#f5f5f5 !important',
        color: '#9e9e9e',
        fontStyle: 'italic',
    },
    '& .status-toggle': {
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        fontWeight: 'bold',
        textAlign: 'left',
        cursor: 'pointer',
        '&:hover:not(:disabled)': { textDecoration: 'underline' },
        '&:disabled': { cursor: 'default', color: 'inherit' },
    },
    '& .status-toggle--inactive': { color: '#9e9e9e' },
    '& .add-row-cell': {
        backgroundColor: '#e3f2fd !important',
        fontSize: '18px',
        fontWeight: 'bold',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#bbdefb !important' },
    },
    '& .add-row-hint': {
        backgroundColor: '#e3f2fd !important',
        fontStyle: 'italic',
        color: '#1976d2',
    },
} as const;

/** Per-grid column widths plus the ReactGrid onColumnResized handler that maintains them. */
export function useColumnWidths() {
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
    const handleColumnResize = useCallback((columnId: Id, width: number) => {
        setColumnWidths(prev => ({ ...prev, [columnId as string]: width }));
    }, []);
    return { columnWidths, handleColumnResize };
}

interface EditableGridOptions<T> {
    /** Build the row the add-row footer appends. */
    makeNewRow: () => T;
    /** True when a row carries no user data, so the action cell removes it rather than flagging it. */
    isRowEmpty: (row: T) => boolean;
}

/**
 * Owns the working rows an editable billing grid holds, and the action-column contract both grids
 * share. Returns `gridRows` for building the ReactGrid model, `commitRows` for a component's own
 * cell-change handler, and `onActionFocus` for the action-column focus dispatch.
 *
 * The grid keeps its own working state (so the Storybook catalog is interactive with static args)
 * and re-seeds from `rows` when the container swaps the data on fetch/import/save — on a plain edit
 * the container mirrors the same array reference back, so the re-seed re-sets the value the grid
 * already holds and React bails, never clobbering the edit.
 */
export function useEditableGrid<T extends { action?: '' | 'DELETE' }>(
    rows: T[],
    onRowsChange: ((rows: T[]) => void) | undefined,
    { makeNewRow, isRowEmpty }: EditableGridOptions<T>,
) {
    const [gridRows, setGridRows] = useState<T[]>(rows);
    useEffect(() => { setGridRows(rows); }, [rows]);

    const commitRows = useCallback((next: T[]) => {
        setGridRows(next);
        onRowsChange?.(next);
    }, [onRowsChange]);

    /**
     * The action-column focus dispatch: append on the footer cell, remove-if-empty-else-toggle on a
     * data row. Returns true when it handled the location, so a grid with extra affordances (the
     * price grid's add-list column) can delegate here after its own checks. Mutates the shallow row
     * copy on the toggle path, as the grids did inline.
     */
    const onActionFocus = useCallback((location: { rowId: Id; columnId: Id }): boolean => {
        if (location.columnId !== ACTION_COLUMN_ID) return false;

        if (location.rowId === ADD_ROW_FOOTER_ID) {
            commitRows([...gridRows, makeNewRow()]);
            return true;
        }

        if (typeof location.rowId === 'number') {
            const next = [...gridRows];
            const row = next[location.rowId];
            if (!row) return true;
            if (isRowEmpty(row)) {
                next.splice(location.rowId, 1);
            } else {
                row.action = row.action === 'DELETE' ? '' : 'DELETE';
            }
            commitRows(next);
            return true;
        }

        return false;
    }, [gridRows, commitRows, makeNewRow, isRowEmpty]);

    return { gridRows, commitRows, onActionFocus };
}
